import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Link,
  Typography,
} from '@mui/material';
import StoreRoundedIcon from '@mui/icons-material/StoreRounded';
import ContentCutRoundedIcon from '@mui/icons-material/ContentCutRounded';
import SearchIcon from '@mui/icons-material/Search';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import bannerImg from '../../images/pexels-gromakova-92378617-9146943.jpg';
import {
  darkPageBg,
  darkCardBg,
  darkHeroSx,
  darkButtonSx,
  accent,
} from '../components/darkFormStyles';

const GetStarted = () => {
  const navigate = useNavigate();

  const options = [
    {
      icon: <StoreRoundedIcon sx={{ fontSize: 34 }} />,
      title: 'Shop Owner with Shop Barbers',
      description:
        'Register your barbershop and manage your team of barbers. Set up your shop profile, add barbers, and start accepting bookings.',
      action: () => {
        localStorage.setItem('businessType', 'Shop Owner');
        navigate('/register/shop-owner');
      },
      buttonLabel: 'Register as Shop Owner',
    },
    {
      icon: <ContentCutRoundedIcon sx={{ fontSize: 34 }} />,
      title: 'Independent Barber',
      description:
        'Register as a mobile or independent barber. Manage your own schedule, clients, and services without a physical shop.',
      action: () => {
        localStorage.setItem('businessType', 'Barber');
        navigate('/register/barber');
      },
      buttonLabel: 'Register as Independent Barber',
    },
  ];

  return (
    <Box sx={{ bgcolor: darkPageBg, minHeight: '100vh' }}>
      {/* Hero Banner */}
      <Box
        sx={{
          mt: '85px',
          ...darkHeroSx(bannerImg),
          py: 8,
          px: 2,
        }}
      >
        <Typography variant="h3" fontWeight="bold" gutterBottom>
          Get Started with Barbers Limited
        </Typography>
        <Typography
          variant="h6"
          sx={{ maxWidth: 600, mx: 'auto', color: 'rgba(255,255,255,0.75)' }}
        >
          Choose how you'd like to join our platform and start growing your
          business today.
        </Typography>
      </Box>

      {/* Options */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={4} justifyContent="center">
          {options.map((option, idx) => (
            <Grid item xs={12} sm={6} md={5} key={idx}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  bgcolor: darkCardBg,
                  border: '1px solid rgba(255,255,255,0.08)',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.5)',
                  },
                }}
              >
                <CardContent
                  sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    p: 4,
                  }}
                >
                  <Box
                    sx={{
                      width: 68,
                      height: 68,
                      borderRadius: '50%',
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      background:
                        'linear-gradient(135deg, #dd8709 0%, #b86e08 100%)',
                      boxShadow: '0 8px 20px rgba(221,135,9,0.35)',
                    }}
                  >
                    {option.icon}
                  </Box>
                  <Typography
                    variant="h5"
                    fontWeight="bold"
                    gutterBottom
                    sx={{ color: '#fff' }}
                  >
                    {option.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ mb: 3, flexGrow: 1, color: 'rgba(255,255,255,0.65)' }}
                  >
                    {option.description}
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={option.action}
                    sx={{ ...darkButtonSx, py: 1.5 }}
                  >
                    {option.buttonLabel}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Find Barbers Link */}
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              p: 3,
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 2,
              bgcolor: darkCardBg,
            }}
          >
            <SearchIcon sx={{ color: accent }} />
            <Typography
              variant="body1"
              sx={{ color: 'rgba(255,255,255,0.75)' }}
            >
              Looking for a barber near you?{' '}
              <Link
                component={RouterLink}
                to="/find-barbers"
                sx={{
                  color: accent,
                  fontWeight: 'bold',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
              >
                Find Barbers
              </Link>
            </Typography>
          </Box>
        </Box>

        {/* Sign in link */}
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)' }}>
            Already have an account?{' '}
            <Link
              component={RouterLink}
              to="/user-login"
              sx={{ color: accent, fontWeight: 'bold' }}
            >
              Sign In
            </Link>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default GetStarted;
